import {
  Gender,
  normalizeGender,
  convertAgeToMonths,
  getWhoWeightReference,
  getWhoBmiReference,
  getWhoHeightReference,
  getPediatricBMIClassification,
  PediatricBmiClassification,
  calculateWhoZScore,
  calculatePercentileFromZScore
} from "../constants/whoData";

export type { Gender, PediatricBmiClassification };

export interface DirectBiaValues {
  bodyFat?: number | null;
  muscleMass?: number | null;
  boneMass?: number | null;
  leanBodyMass?: number | null;
  protein?: number | null;
  bodyMoisture?: number | null;
  visceralFat?: number | null;
  subcutaneousFat?: number | null;
}

export interface BodyMetricsResult {
  age: number | "-";
  ageInMonths: number;
  exactAgeYears: number;
  bmi: number | "-";
  obesityLevel: string;
  bmiZScore: number | "-";
  bmiPercentile: number | "-";
  bodyFat: number | "-";
  isBodyFatEstimated: boolean;
  leanBodyMass: number | "-";
  muscleMass: number | "-";
  boneMass: number | "-";
  bmr: number | "-";
  bmrMethod: string;
  protein: number | "-";
  bodyMoisture: number | "-";
  visceralFat: number | "-";
  subcutaneousFat: number | "-";
  standardWeight: number | "-";
  standardWeightMethod: string;
  bodyType: string;
  maxHeartRate: number | "-";
  restingHeartRate: number;
  targetHeartRate: {
    fatBurnZone: { min: number; max: number };
    cardioZone: { min: number; max: number };
  } | null;
}

/* ---------------- DATE PARSING UTILITY ---------------- */
/**
 * Safely parses any date representation:
 * - Date instance
 * - ISO string: "2015-05-15" or "2015-05-15T12:00:00"
 * - DD/MM/YYYY or DD-MM-YYYY: "15/05/2015", "15-05-2015"
 * - MM/DD/YYYY: "05/15/2015"
 * - Numeric timestamp / Excel serial date
 */
export const parseFlexibleDate = (val?: string | Date | number | null): Date | null => {
  if (!val || val === "-" || val === "N/A") return null;
  if (val instanceof Date) {
    return isNaN(val.getTime()) ? null : val;
  }

  if (typeof val === "number") {
    // Excel serial date (e.g. 42139)
    if (val > 20000 && val < 75000) {
      return new Date(Math.round((val - 25569) * 86400 * 1000));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }

  const str = String(val).trim();
  if (!str) return null;

  // Handle DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    const year = parseInt(dmyMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Handle YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
  if (ymdMatch) {
    const year = parseInt(ymdMatch[1], 10);
    const month = parseInt(ymdMatch[2], 10) - 1;
    const day = parseInt(ymdMatch[3], 10);
    const d = new Date(year, month, day);
    if (!isNaN(d.getTime())) return d;
  }

  // Fallback to standard Date.parse
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

/* ---------------- AGE CALCULATION ---------------- */
/**
 * Calculates integer age at measurement date (completed calendar years).
 * If measurementDate is missing, current date is used.
 */
export const calculateAgeFromDOB = (
  dob?: string | Date | number | null,
  measurementDate?: string | Date | number | null
): number => {
  const birth = parseFlexibleDate(dob);
  if (!birth) return 0;

  const ref = parseFlexibleDate(measurementDate) || new Date();

  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && ref.getDate() < birth.getDate())
  ) {
    age--;
  }

  return age < 0 ? 0 : age;
};

/**
 * Fractional age in decimal years (e.g., 12.45) – essential for accurate WHO and trend chart positioning
 */
export const calculateAgeInYears = (
  dob?: string | Date | number | null,
  referenceDate?: string | Date | number | null
): number => {
  const birth = parseFlexibleDate(dob);
  if (!birth) return 0;

  const ref = parseFlexibleDate(referenceDate) || new Date();

  const diffMs = ref.getTime() - birth.getTime();
  if (diffMs <= 0) return 0;
  return +(diffMs / (1000 * 60 * 60 * 24 * 365.2425)).toFixed(2);
};

/* ---------------- BMI ---------------- */
/**
 * Calculates Body Mass Index: weight (kg) / [height (m)]^2
 */
export const calculateBMI = (
  heightCm: number,
  weightKg: number
): number => {
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) return 0;

  const heightM = heightCm / 100;
  return +(weightKg / (heightM * heightM)).toFixed(2);
};

/**
 * Evaluates BMI classification.
 * For pediatric cohort (ages 5–19 years / 61–228 months), utilizes the official WHO 2007 BMI-for-age Z-score methodology.
 * For adults (> 19 years), falls back to standard WHO adult BMI cutoffs.
 */
export const getObesityLevel = (
  bmi: number,
  ageInMonths?: number,
  gender?: string | null
): string => {
  if (!bmi || bmi <= 0) return "-";

  // Pediatric WHO 2007 BMI-for-Age (5 to 19 years = 60 to 228 months)
  if (typeof ageInMonths === "number" && ageInMonths >= 60 && ageInMonths <= 228) {
    const res = getPediatricBMIClassification(bmi, ageInMonths, gender);
    if (res.isAvailable) {
      return res.classification;
    }
  }

  // Fallback for adult ages (> 19y) or when demographic age is not available
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Overweight";
  return "Obese";
};

/* ---------------- PEDIATRIC BMR (SCHOFIELD EQUATIONS) ---------------- */
/**
 * Calculates Basal Metabolic Rate (BMR in kcal/day)
 * using authoritative Schofield (1985) / FAO-WHO-UNU (1985) pediatric equations.
 *
 * References:
 * - Schofield WN. Predicting basal metabolic rate, new standards and review of previous work.
 *   Human Nutrition: Clinical Nutrition 1985; 39C (Suppl 1): 5-41.
 * - Joint FAO/WHO/UNU Expert Consultation. Energy and protein requirements.
 *   WHO Technical Report Series 724. Geneva: WHO, 1985.
 */
export const calculatePediatricBMR = (
  weightKg: number,
  heightCm: number,
  ageYears: number,
  gender: Gender
): { bmr: number; method: string } => {
  if (!weightKg || weightKg <= 0 || !heightCm || heightCm <= 0) {
    return { bmr: 0, method: "N/A" };
  }

  const isMale = gender === "Male";

  // Age Bracket 1: Ages 3 to 10 years (36 to 120 months)
  if (ageYears < 10) {
    const bmr = isMale
      ? 19.59 * weightKg + 1.303 * heightCm + 414.9
      : 16.969 * weightKg + 1.618 * heightCm + 371.2;
    return {
      bmr: Math.round(bmr),
      method: `Schofield Pediatric (3-10y ${gender})`
    };
  }

  // Age Bracket 2: Ages 10 to 18 years (121 to 216 months)
  if (ageYears < 18) {
    const bmr = isMale
      ? 16.25 * weightKg + 1.372 * heightCm + 515.5
      : 8.365 * weightKg + 4.65 * heightCm + 200.0;
    return {
      bmr: Math.round(bmr),
      method: `Schofield Pediatric (10-18y ${gender})`
    };
  }

  // Age Bracket 3: Ages 18 to 30 years (including age 19 / 217-228 months)
  const bmr = isMale
    ? 15.057 * weightKg + 1.004 * heightCm + 705.8
    : 13.623 * weightKg + 2.83 * heightCm + 98.2;
  return {
    bmr: Math.round(bmr),
    method: `Schofield Young Adult (18-30y ${gender})`
  };
};

/* ---------------- PEDIATRIC BODY FAT % ---------------- */
/**
 * Calculates Body Fat % estimation using validated Deurenberg Pediatric Formula:
 *   Body Fat % = 1.51 * BMI - 0.70 * Age - 3.6 * Sex + 1.4
 * (where Sex = 1 for Male, 0 for Female; Age in decimal years)
 *
 * References:
 * - Deurenberg P et al. Assessment of body fat percentage by skinfold thickness measurements.
 *   British Journal of Nutrition 1990; 63(2): 293-303.
 * - Deurenberg P et al. Body mass index as a measure of body fatness: age- and sex-specific prediction formulas.
 *   British Journal of Nutrition 1991; 65(2): 105-114.
 */
export const calculatePediatricBodyFat = (
  bmi: number,
  ageYears: number,
  gender: Gender
): number => {
  if (!bmi || bmi <= 0) return 0;

  const sex = gender === "Male" ? 1 : 0;
  const age = ageYears > 0 ? ageYears : 10;

  let rawFat: number;
  if (age < 18) {
    rawFat = 1.51 * bmi - 0.70 * age - 3.6 * sex + 1.4;
  } else {
    // Young adult formula for 18-19y
    rawFat = 1.20 * bmi + 0.23 * age - 10.8 * sex - 5.4;
  }

  // Physiological bounds clamping [3%, 65%]
  if (rawFat < 3.0) rawFat = 3.0;
  if (rawFat > 65.0) rawFat = 65.0;

  return +rawFat.toFixed(2);
};

/* ---------------- STANDARD / NORMATIVE WEIGHT ---------------- */
/**
 * Computes Standard / Normative Weight for pediatric age and sex:
 * - Ages 5-10y (61-120 months): Exact WHO 2007 Weight-for-Age 50th percentile (P50).
 * - Ages 11-19y (121-228 months): Height-adjusted normative weight = WHO BMI P50 * (Height/100)^2.
 */
export const calculateStandardWeight = (
  heightCm: number,
  ageInMonths: number,
  gender: Gender
): { standardWeight: number | "-"; method: string } => {
  if (!heightCm || heightCm <= 0 || !ageInMonths || ageInMonths <= 0) {
    return { standardWeight: "-", method: "N/A" };
  }

  // 5-10 years (61 to 120 months): Use WHO Weight-for-Age P50
  if (ageInMonths <= 120) {
    const wRef = getWhoWeightReference(gender, ageInMonths);
    if (wRef && typeof wRef.p50 === "number") {
      return {
        standardWeight: wRef.p50,
        method: `WHO Weight-for-age Median (${gender})`
      };
    }
  }

  // 11-19 years (121 to 228 months): Use WHO BMI P50 * (Height/100)^2
  if (ageInMonths <= 228) {
    const bmiRef = getWhoBmiReference(gender, ageInMonths);
    if (bmiRef && typeof bmiRef.p50 === "number") {
      const heightM = heightCm / 100;
      const stdWeight = +(bmiRef.p50 * heightM * heightM).toFixed(2);
      return {
        standardWeight: stdWeight,
        method: `WHO Height-Adjusted Median BMI (${gender})`
      };
    }
  }

  return { standardWeight: "-", method: "Unavailable outside 5-19y range" };
};

/* ---------------- HEART RATE ---------------- */
export const calculateMaxHeartRate = (age: number): number => {
  return 220 - age;
};

export const calculateTargetHeartRate = (
  age: number,
  restingHR: number = 70
) => {
  const maxHR = calculateMaxHeartRate(age);

  return {
    fatBurnZone: {
      min: Math.round((maxHR - restingHR) * 0.5 + restingHR),
      max: Math.round((maxHR - restingHR) * 0.7 + restingHR),
    },
    cardioZone: {
      min: Math.round((maxHR - restingHR) * 0.7 + restingHR),
      max: Math.round((maxHR - restingHR) * 0.85 + restingHR),
    },
  };
};

/* ---------------- BODY METRICS MASTER ENGINE ---------------- */
/**
 * Master body metrics calculation engine.
 * Computes pediatric anthropometry, bio-composition, WHO Z-scores, and metabolism metrics.
 * Prioritizes direct smart-device BIA measurements when provided.
 */
export const calculateBodyMetrics = (
  heightCm: number,
  weightKg: number,
  dob: string | Date,
  gender: Gender,
  restingHR: number = 70,
  measurementDate?: string | Date,
  directBia?: DirectBiaValues
): BodyMetricsResult => {
  // Validate gender fallback
  const safeGender: Gender = normalizeGender(gender) || "Male";

  // Age calculations
  const integerAge = calculateAgeFromDOB(dob, measurementDate);
  const exactAgeYears = calculateAgeInYears(dob, measurementDate);
  const ageInMonths = convertAgeToMonths(exactAgeYears > 0 ? exactAgeYears : integerAge);

  const bmi = calculateBMI(heightCm, weightKg);

  if (bmi <= 0 || heightCm <= 0 || weightKg <= 0) {
    return {
      age: integerAge > 0 ? integerAge : "-",
      ageInMonths,
      exactAgeYears,
      bmi: "-",
      obesityLevel: "-",
      bmiZScore: "-",
      bmiPercentile: "-",
      bodyFat: "-",
      isBodyFatEstimated: false,
      leanBodyMass: "-",
      muscleMass: "-",
      boneMass: "-",
      bmr: "-",
      bmrMethod: "N/A",
      protein: "-",
      bodyMoisture: "-",
      visceralFat: "-",
      subcutaneousFat: "-",
      standardWeight: "-",
      standardWeightMethod: "N/A",
      bodyType: "-",
      maxHeartRate: integerAge > 0 ? calculateMaxHeartRate(integerAge) : "-",
      restingHeartRate: restingHR || 70,
      targetHeartRate: integerAge > 0 ? calculateTargetHeartRate(integerAge, restingHR || 70) : null
    };
  }

  // 1. Pediatric WHO BMI Classification & Z-Score
  const bmiClassification = getPediatricBMIClassification(bmi, ageInMonths, safeGender);
  const obesityLevel = bmiClassification.isAvailable
    ? bmiClassification.classification
    : getObesityLevel(bmi);

  const bmiZScore = bmiClassification.isAvailable && bmiClassification.zScore !== null
    ? bmiClassification.zScore
    : "-";

  const bmiPercentile = bmiClassification.isAvailable && bmiClassification.percentile !== null
    ? bmiClassification.percentile
    : "-";

  // 2. Body Fat % (Priority: Direct BIA measurement > Validated Pediatric Deurenberg)
  let bodyFat: number;
  let isBodyFatEstimated = false;

  if (directBia?.bodyFat && typeof directBia.bodyFat === "number" && directBia.bodyFat > 0) {
    bodyFat = +directBia.bodyFat.toFixed(2);
    isBodyFatEstimated = false;
  } else {
    bodyFat = calculatePediatricBodyFat(bmi, exactAgeYears || integerAge, safeGender);
    isBodyFatEstimated = true;
  }

  // 3. Lean Body Mass (Priority: Direct BIA > Derived from weight & body fat)
  let leanBodyMass: number;
  if (directBia?.leanBodyMass && typeof directBia.leanBodyMass === "number" && directBia.leanBodyMass > 0) {
    leanBodyMass = +directBia.leanBodyMass.toFixed(2);
  } else {
    const rawLbm = +(weightKg * (1 - bodyFat / 100)).toFixed(2);
    leanBodyMass = rawLbm > 0 ? rawLbm : +(weightKg * 0.7).toFixed(2);
  }

  // 4. BMR (Schofield / FAO-WHO-UNU Pediatric Equation)
  const bmrResult = calculatePediatricBMR(weightKg, heightCm, exactAgeYears || integerAge, safeGender);

  // 5. Standard / Normative Weight (WHO P50)
  const stdWeightResult = calculateStandardWeight(heightCm, ageInMonths, safeGender);

  // 6. Secondary Bio-Composition Metrics (Direct BIA > Physiologically calibrated estimates)
  // Muscle Mass (kg)
  const muscleMass = (directBia?.muscleMass && typeof directBia.muscleMass === "number" && directBia.muscleMass > 0)
    ? +directBia.muscleMass.toFixed(2)
    : +(leanBodyMass * 0.5).toFixed(2);

  // Bone Mass (kg) - Bone mineral mass in children is approximately 4% of body weight
  const boneMass = (directBia?.boneMass && typeof directBia.boneMass === "number" && directBia.boneMass > 0)
    ? +directBia.boneMass.toFixed(2)
    : +(weightKg * 0.04).toFixed(2);

  // Protein % - Protein content in human tissue is ~18% of Lean Body Mass
  const protein = (directBia?.protein && typeof directBia.protein === "number" && directBia.protein > 0)
    ? +directBia.protein.toFixed(2)
    : +((leanBodyMass * 0.18 / weightKg) * 100).toFixed(2);

  // Body Moisture % (Total Body Water %) - TBW in children is ~73% of LBM
  const bodyMoisture = (directBia?.bodyMoisture && typeof directBia.bodyMoisture === "number" && directBia.bodyMoisture > 0)
    ? +directBia.bodyMoisture.toFixed(2)
    : Math.min(75, Math.max(45, +((leanBodyMass * 0.73 / weightKg) * 100).toFixed(2)));

  // Visceral Fat (Rating Level 1-12 based on WHO BMI Z-score)
  let visceralFat: number;
  if (directBia?.visceralFat && typeof directBia.visceralFat === "number" && directBia.visceralFat > 0) {
    visceralFat = directBia.visceralFat;
  } else if (typeof bmiZScore === "number") {
    visceralFat = bmiZScore < -2 ? 1 : bmiZScore <= 1 ? 2 : bmiZScore <= 2 ? 5 : 8;
  } else {
    visceralFat = bmi < 18.5 ? 1 : bmi < 25 ? 2 : bmi < 30 ? 5 : 8;
  }

  // Subcutaneous Fat % (~85% of total body fat)
  const subcutaneousFat = (directBia?.subcutaneousFat && typeof directBia.subcutaneousFat === "number" && directBia.subcutaneousFat > 0)
    ? +directBia.subcutaneousFat.toFixed(2)
    : +(bodyFat * 0.85).toFixed(2);

  // Body Type (Preserved for compatibility)
  const bodyType = bodyFat < 15 ? "Athletic" : bodyFat < 25 ? "Average" : "Obese";

  // Heart Rate
  const maxHeartRate = integerAge > 0 ? calculateMaxHeartRate(integerAge) : "-";
  const targetHeartRate = integerAge > 0 ? calculateTargetHeartRate(integerAge, restingHR || 70) : null;

  return {
    age: integerAge > 0 ? integerAge : "-",
    ageInMonths,
    exactAgeYears,
    bmi,
    obesityLevel,
    bmiZScore,
    bmiPercentile,
    bodyFat,
    isBodyFatEstimated,
    leanBodyMass,
    muscleMass,
    boneMass,
    bmr: bmrResult.bmr,
    bmrMethod: bmrResult.method,
    protein,
    bodyMoisture,
    visceralFat,
    subcutaneousFat,
    standardWeight: stdWeightResult.standardWeight,
    standardWeightMethod: stdWeightResult.method,
    bodyType,
    maxHeartRate,
    restingHeartRate: restingHR || 70,
    targetHeartRate
  };
};
